import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateHousing() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    hasHouse: '',
    houseOwnership: '',
    otherHouseOwnership: '',
    houseType: '',
    otherHouseType: '',
    houseCondition: '',
    otherHouseCondition: '',
    hasLand: '',
    landOwnership: '',
    otherLandOwnership: '',
    monthlyIncome: '',
    otherMonthlyIncome: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message:
          'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getHousingDetails(recordId, sheetId);

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load housing details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const resolveOther = (val, options) =>
        val && !options.includes(val) ? 'Other' : val;
      const resolveOtherText = (val, options) =>
        val && !options.includes(val) ? val : '';

      const houseOwnershipOpts = response.legend?.houseOwnership || [];
      const houseTypeOpts = response.legend?.houseType || [];
      const houseConditionOpts = response.legend?.houseCondition || [];
      const landOwnershipOpts = response.legend?.landOwnership || [];
      const monthlyIncomeOpts = response.legend?.monthlyIncome || [];

      const rawHouseOwnership = response.record.houseOwnership || '';
      const rawHouseType = response.record.houseType || '';
      const rawHouseCondition = response.record.houseCondition || '';
      const rawLandOwnership = response.record.landOwnership || '';
      const rawMonthlyIncome = response.record.monthlyIncome || '';

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        hasHouse: response.record.hasHouse || '',
        houseOwnership: resolveOther(rawHouseOwnership, houseOwnershipOpts),
        otherHouseOwnership: resolveOtherText(rawHouseOwnership, houseOwnershipOpts),
        houseType: resolveOther(rawHouseType, houseTypeOpts),
        otherHouseType: resolveOtherText(rawHouseType, houseTypeOpts),
        houseCondition: resolveOther(rawHouseCondition, houseConditionOpts),
        otherHouseCondition: resolveOtherText(rawHouseCondition, houseConditionOpts),
        hasLand: response.record.hasLand || '',
        landOwnership: resolveOther(rawLandOwnership, landOwnershipOpts),
        otherLandOwnership: resolveOtherText(rawLandOwnership, landOwnershipOpts),
        monthlyIncome: resolveOther(rawMonthlyIncome, monthlyIncomeOpts),
        otherMonthlyIncome: resolveOtherText(rawMonthlyIncome, monthlyIncomeOpts),
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };

    if (name === 'hasLand' && value === 'No') {
      updates.landOwnership = '';
      updates.otherLandOwnership = '';
    }

    const clearMap = {
      houseOwnership: 'otherHouseOwnership',
      houseType: 'otherHouseType',
      houseCondition: 'otherHouseCondition',
      landOwnership: 'otherLandOwnership',
      monthlyIncome: 'otherMonthlyIncome',
    };
    if (value !== 'Other' && clearMap[name]) updates[clearMap[name]] = '';

    setFormData({ ...formData, ...updates });
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const resolveField = (field, otherField) =>
        formData[field] === 'Other' && formData[otherField]
          ? formData[otherField]
          : formData[field];

      const updateData = {
        uniqueId: recordData.uniqueId,
        hasHouse: formData.hasHouse,
        houseOwnership: resolveField('houseOwnership', 'otherHouseOwnership'),
        houseType: resolveField('houseType', 'otherHouseType'),
        houseCondition: resolveField('houseCondition', 'otherHouseCondition'),
        hasLand: formData.hasLand,
        landOwnership:
          formData.hasLand === 'Yes'
            ? resolveField('landOwnership', 'otherLandOwnership')
            : '',
        monthlyIncome: resolveField('monthlyIncome', 'otherMonthlyIncome'),
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateHousingDetails(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Housing details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/disability/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/personal/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={4}
        title="Update Housing & Ownership Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading housing details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={4}
        title="Update Housing & Ownership Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load housing details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={4}
      title="Update Housing & Ownership Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">
          Housing & Ownership Details (Editable)
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Do you have a house?</Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="hasHouse"
              value="Yes"
              checked={formData.hasHouse === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="hasHouse"
              value="No"
              checked={formData.hasHouse === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>House Ownership</Form.Label>
          <SelectWithOther
            name="houseOwnership"
            value={formData.houseOwnership}
            otherName="otherHouseOwnership"
            otherValue={formData.otherHouseOwnership}
            options={legendData?.houseOwnership || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            otherPlaceholder="Please specify house ownership"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Type of the House</Form.Label>
          <SelectWithOther
            name="houseType"
            value={formData.houseType}
            otherName="otherHouseType"
            otherValue={formData.otherHouseType}
            options={legendData?.houseType || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            otherPlaceholder="Please specify house type"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>The Condition of the House</Form.Label>
          <SelectWithOther
            name="houseCondition"
            value={formData.houseCondition}
            otherName="otherHouseCondition"
            otherValue={formData.otherHouseCondition}
            options={legendData?.houseCondition || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            otherPlaceholder="Please specify house condition"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Do you have a Land?</Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="hasLand"
              value="Yes"
              checked={formData.hasLand === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="hasLand"
              value="No"
              checked={formData.hasLand === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {/* Conditional Field - Only show if hasLand is Yes */}
        {formData.hasLand === 'Yes' && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Nature of Ownership of the Land</Form.Label>
            <SelectWithOther
              name="landOwnership"
              value={formData.landOwnership}
              otherName="otherLandOwnership"
              otherValue={formData.otherLandOwnership}
              options={legendData?.landOwnership || []}
              onChange={handleInputChange}
              disabled={!canEdit}
              otherPlaceholder="Please specify nature of land ownership"
            />
            <Form.Text className="text-muted">
              Only displayed if you own land.
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Family Monthly Average Income</Form.Label>
          <SelectWithOther
            name="monthlyIncome"
            value={formData.monthlyIncome}
            otherName="otherMonthlyIncome"
            otherValue={formData.otherMonthlyIncome}
            options={legendData?.monthlyIncome || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            otherPlaceholder="Please specify monthly income range"
          />
        </Form.Group>
      </Form>
    </UpdateLayout>
  );
}

export default UpdateHousing;
